import React, {useEffect, useMemo, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {Button, Input} from '../../../sharedComponents/components'
import {colors} from '../../../sharedComponents/colors'

import styled from 'styled-components'

import {AssetData} from '../../../state/rest'
import {useAppState} from '../../../state'
import AssetUploaderSmall from '../../../Components/Molecules/UploadImage'
import {IconCloseSmall} from '../../../Assets/Icons/TinyIcons'
import {SmartAsset} from '../../../Components/Atoms/ImageLoader'
import InputWithTags from './InputWithTags'
import {LabelCheckbox} from '../../../Components/Atoms/Forms'
import IsUserHealthInspector from '../../../hooks/userIsHealthInspector'

//Special placeholder for Finnair kitchen tasks, as they want AY-number instead of name in completed by field
const FINNAIR_KITCHEN_CHAIN_ID = '68b5434d-2207-474a-b0f3-942da9307adf'

const PerformTaskFormContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const RemoveTaskArea = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 1rem;
`

const Divider = styled.hr`
  width: 100%;
  height: 1px;
  background-color: ${colors.system.grey_5};
  border: none;
`

const DangerousButton = styled(Button)`
  color: red;
  background-color: white;
  border-color: red;
`

const ThumbContainer = styled.div`
  width: 140px;
  height: 125px;
  flex: 0 0 auto;
  position: relative;
  border: 1px solid ${colors.system.grey_5};
  border-radius: 8px;
  overflow: hidden;
  cursor: pointer;
  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
    display: block;
  }
  .remove-overlay {
    position: absolute;
    top: 4px;
    right: 4px;
    z-index: 50;
  }
`

const CloseIconButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 24px;
  height: 24px;
  border: 1px solid ${colors.system.grey_5};
  border-radius: 12px;
  background: #fff;
  cursor: pointer;
  padding: 0;
  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`

const UploadCounter = styled.div`
  font-size: 0.825rem;
  color: ${colors.system.grey_50};
  display: flex;
  align-items: center;
  justify-content: flex-start;
  padding-right: 0.75rem;
  margin-top: 7px;
`

const CompletedByField = styled.div`
  display: flex;
  flex-direction: column;
`

const CharacterCounter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 0.25rem;
  color: ${colors.system.grey_50};
  font-size: 0.75rem;
`

const InlineThumb = ({
  asset,
  onRemove,
  disabled
}: {
  asset: AssetData
  onRemove: (asset: string) => void
  disabled: boolean
}) => {
  const {actions} = useAppState()
  const {t} = useTranslation('common')

  const openAssetInNewTab = async () => {
    try {
      const d = await actions.getDownloadUrl(asset.id)
      if (d && (d as any).downloadURL) {
        window.open((d as any).downloadURL, '_blank', 'noopener')
      }
    } catch (err) {
      console.error('Error opening asset', err)
    }
  }

  return (
    <ThumbContainer title={asset.id} onClick={openAssetInNewTab}>
      <SmartAsset asset={asset as any} />
      <div className="remove-overlay">
        <CloseIconButton
          type="button"
          onMouseDown={e => {
            e.preventDefault()
            e.stopPropagation()
          }}
          onClick={e => {
            e.preventDefault()
            e.stopPropagation()
            onRemove(asset.id)
          }}
          disabled={disabled}
          aria-label={t('common:buttons.remove', 'Remove')}
        >
          <IconCloseSmall />
        </CloseIconButton>
      </div>
    </ThumbContainer>
  )
}

interface PerformTaskFormProps {
  onInputChange: (key: 'remarks' | 'reportedActor' | 'relatedSites', value: string | string[]) => void
  payload: any
  handleRemove?: () => void
  onAssetSaved?: (asset?: AssetData) => void
  onAssetRemoved: (asset: string) => void
  setValue: any
  confirm?: string
  handleSubmitAndAddNew?: () => void
  handleSubmit?: (dontClose?: boolean) => Promise<void> | void
  disabled?: boolean
  hasCurrentEvent?: boolean
  currentEventId?: string
}

/**
 * Component for displaying elements that are same independent of task type
 */

const PerformTaskForm = ({
  onAssetSaved,
  onAssetRemoved,
  payload,
  onInputChange,
  handleRemove,
  setValue,
  confirm,
  handleSubmitAndAddNew,
  handleSubmit,
  disabled,
  hasCurrentEvent,
  currentEventId
}: PerformTaskFormProps) => {
  const {t} = useTranslation(['tasks', 'common'])
  const {task, reportedActor, remarks, relatedSites} = payload
  const relatedSiteSuggestions = task.relatedSiteSuggestions || task.relatedSites || []
  const {state, actions} = useAppState()
  const {isHealthInspector} = IsUserHealthInspector()

  const [valid, setValid] = useState(!!!confirm)

  // ONLY allow user who is not basic user to remove task: site manager, chain manager, admin
  const allowRemoveTask = state.me && state.site && state.me.accessRights.write.sites.includes(state.site.id)
  const currentImageCount = payload.assets ? payload.assets.length : 0
  const disableUploader = currentImageCount >= 3
  const completedByPlaceholder =
    state.selectedChainId && state.selectedChainId === FINNAIR_KITCHEN_CHAIN_ID
      ? t('tasks:labels.completedBy.placeholderFinnair', 'AY-number')
      : t('tasks:labels.completedBy.placeholder', 'Name')
  const selectedChain = state.selectedChainId ? state.chainsById[state.selectedChainId] : undefined
  const isCompletedByMandatoryForAllTasks = selectedChain?.completedByMandatoryForAllTasks ?? false
  return (
    <PerformTaskFormContainer>
      {!task.actorSuggestions ? (
        <CompletedByField>
          <Input
            data-cy="task-modal-input-completed-by"
            name={t('tasks:labels.columnNameCompletedBy', 'Completed By')}
            label={t('tasks:labels.columnNameCompletedBy', 'Completed By')}
            value={reportedActor || task.reportedActor || ''}
            onChange={(e: any) => onInputChange('reportedActor', e.target.value)}
            placeholder={completedByPlaceholder}
            disabled={isHealthInspector}
            required={isCompletedByMandatoryForAllTasks}
            maxLength={100}
          />
          <CharacterCounter>{`${(reportedActor || task.reportedActor || '').length}/100`}</CharacterCounter>
        </CompletedByField>
      ) : (
        <InputWithTags
          label={t('tasks:labels.columnNameCompletedBy', 'Completed By')}
          placeholder={completedByPlaceholder}
          value={task.reportedActor || ''}
          defaultSuggestions={task.actorSuggestions || []}
          onChange={setValue}
          keyName="reportedActor"
          disabled={isHealthInspector}
          required={isCompletedByMandatoryForAllTasks}
          maxCharacters={100}
        />
      )}
      {(!!relatedSiteSuggestions.length || !!relatedSites?.length) && (
        <InputWithTags
          label={t('tasks:labels.relatedSitesCompletion', 'Related site to report')}
          placeholder={t('tasks:labels.selectRelatedSite', 'Select a related site')}
          value={relatedSites?.[0] || ''}
          defaultSuggestions={relatedSiteSuggestions}
          savedSuggestions={relatedSites}
          onChange={(_, selected) => onInputChange('relatedSites', selected)}
          keyName="relatedSites"
          disabled={isHealthInspector}
        />
      )}
      <Input
        name="remarks"
        label={t('tasks:labels.otherInformation', 'Other Information')}
        value={remarks || ''}
        multiline
        onChange={(e: any) => onInputChange('remarks', e.target.value)}
        disabled={isHealthInspector}
        placeholder={t('tasks:labels.writeNotes', 'Add notes about the task')}
      />
      <div style={{display: 'flex', alignItems: 'center', gap: '0.5rem'}}>
        {!!onAssetSaved && !isHealthInspector && (
          <AssetUploaderSmall
            onAssetSaved={onAssetSaved}
            savedAsset={undefined}
            multiple={true}
            maxFiles={Math.max(0, 3 - currentImageCount)}
            disabled={disableUploader}
          />
        )}
        {/** For waste tasks, always show Save and Add New inline button (if handler provided) */}
        {payload?.task?.taskType?.id === 'waste' && !!handleSubmitAndAddNew && (
          <Button
            buttonStyle="secondary"
            buttonSize="small"
            data-cy="perform-task-save-add-new-inline"
            onClick={async () => {
              try {
                if (handleSubmitAndAddNew) await handleSubmitAndAddNew()
              } catch (e) {
                console.error('Save and add new failed', e)
              }
            }}
            disabled={disabled || isHealthInspector}
          >
            {t('tasks:actions.saveAndAdd', 'Save and add new')}
          </Button>
        )}
      </div>
      {currentImageCount > 0 && <Divider />}
      {/* Thumbnails: show primary asset and any pending/attached thumbnails */}
      <div style={{display: 'flex', gap: '0.5rem', flexWrap: 'wrap', alignItems: 'flex-start'}}>
        {payload.assets &&
          payload.assets.map((a: AssetData) => (
            <InlineThumb key={a.id} asset={a} onRemove={() => onAssetRemoved(a.id)} disabled={isHealthInspector} />
          ))}
      </div>
      <div>
        <UploadCounter>{`${currentImageCount}/3`}</UploadCounter>
      </div>
      {!!handleRemove && allowRemoveTask && (
        <div>
          <Divider />
          {!!confirm && (
            <LabelCheckbox
              flipped={false}
              checked={valid}
              onClick={() => setValid(s => !s)}
              label={confirm}
              name="confirmModal"
            />
          )}
          <RemoveTaskArea>
            <DangerousButton onClick={() => handleRemove()} buttonSize="small" disabled={!valid || isHealthInspector}>
              {t('common:buttons.removeTask', 'Remove task')}
            </DangerousButton>
          </RemoveTaskArea>
        </div>
      )}
    </PerformTaskFormContainer>
  )
}

export default PerformTaskForm
